import * as React from 'react'
import { Briefcase, Loader2, UserPlus, Mail, Trash2, X, Users } from 'lucide-react'
import {
  useAddEventClient,
  useRemoveEventClient,
  useInviteClient,
  useDeactivateClient,
  useDeleteInvitedClient,
  useReactivateClient,
  useClientSearch,
  useSetClientPhone,
} from '@/hooks/useEvents'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useAuth } from '@/auth/AuthContext'
import { can, reachesCompany } from '@/auth/access'
import { EventEmailLog } from '@/components/EventEmailLog'
import { ClientToPlannerDialog } from '@/components/ClientToPlannerDialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Field } from '@/components/ui/field'
import { InlineText } from '@/components/ui/inline-text'
import { FormError } from '@/components/ui/form-error'
import { Card } from '@/components/ui/card'
import { Callout } from '@/components/ui/callout'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { UserStatusBadge, inviteActionLabel } from '@/components/UserStatusBadge'
import { newEventClientInputSchema } from '@lib/input/event.input'
import { phoneSchema } from '@lib/validators/phone.validator'
import type { EventDetailDto, EventClientDto } from '@lib/dto/event.dto'
import { possessive } from '@lib/helpers/format'

/**
 * A client's phone, edited where it is read: it looks like the email line above
 * it until clicked, and saves when the field is left or Enter is pressed.
 * Emptying it clears the phone.
 */
function ClientPhone({ eventId, client }: { eventId: string; client: EventClientDto }) {
  const save = useSetClientPhone(eventId)
  const [draft, setDraft] = React.useState(client.phone ?? '')
  const [problem, setProblem] = React.useState<string | null>(null)

  function commit(): void {
    const trimmed = draft.trim()
    if (trimmed === (client.phone ?? '')) return
    if (trimmed === '') {
      save.mutate({ clientId: client.id, phone: null }, { onError: (e) => setProblem(requestErrorMessage(e)) })
      return
    }
    const parsed = phoneSchema.safeParse(trimmed)
    if (!parsed.success) {
      setProblem(parsed.error.issues[0]?.message ?? 'Enter a phone number.')
      return
    }
    save.mutate({ clientId: client.id, phone: parsed.data }, { onError: (e) => setProblem(requestErrorMessage(e)) })
  }

  return (
    <>
      <InlineText
        variant="subtle"
        className="text-xs"
        aria-label={`${possessive(client.name)} phone`}
        placeholder="Add a phone"
        value={draft}
        disabled={save.isPending}
        onChange={(e) => {
          setDraft(e.target.value)
          setProblem(null)
        }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur()
          if (e.key === 'Escape') {
            setDraft(client.phone ?? '')
            setProblem(null)
          }
        }}
      />
      {problem ? <FormError size="sm">{problem}</FormError> : null}
    </>
  )
}

/**
 * One client contact: identity, status, and per-contact actions. Taking them
 * off the event follows Events; inviting, deactivating and their phone follow
 * Clients, since they change the person rather than the event.
 *
 * A refused action is said on the row. The two that change the person rather
 * than this event, deleting an invitation and deactivating, are confirmed first.
 */
function ContactRow({
  event,
  client,
  canEdit,
  canManageClients,
  onPlannerUnsent,
}: {
  event: EventDetailDto
  client: EventClientDto
  canEdit: boolean
  canManageClients: boolean
  /** A conversion went through without its invitation. Said by the panel: this row goes once they are the planner. */
  onPlannerUnsent: (name: string) => void
}) {
  const { user } = useAuth()
  // Deleting a client removes the person and cascades their links to every
  // event they are on, including at venues someone on assigned venues does not
  // manage — so it needs a role that reaches the whole company. Hidden rather
  // than left to fail: an action offered and then refused is how "Forbidden
  // resource" ended up printed under somebody's email address.
  const canDeleteClients = canManageClients && can(user, 'clients:edit') && reachesCompany(user)
  const remove = useRemoveEventClient(event.id)
  const invite = useInviteClient(event.id)
  const deactivate = useDeactivateClient(event.id)
  const deleteInvited = useDeleteInvitedClient(event.id)
  const reactivate = useReactivateClient(event.id)
  const [msg, setMsg] = React.useState<string | null>(null)
  const [converting, setConverting] = React.useState(false)
  // Which question, kept while the dialog closes so its words do not change on the way out.
  const [asking, setAsking] = React.useState<'delete' | 'deactivate'>('deactivate')
  const [confirming, setConfirming] = React.useState(false)
  const [confirmError, setConfirmError] = React.useState<string | null>(null)
  const busy =
    remove.isPending || invite.isPending || deactivate.isPending || deleteInvited.isPending || reactivate.isPending

  /** What a row action does with its answer: a refusal is said on the row, and a success clears the last one. */
  const said = { onSuccess: () => setMsg(null), onError: (e: Error) => setMsg(requestErrorMessage(e)) }

  function confirmStatusChange(): void {
    const options = {
      onSuccess: () => {
        setConfirming(false)
        setConfirmError(null)
        setMsg(null)
      },
      onError: (e: Error) => setConfirmError(requestErrorMessage(e)),
    }
    if (asking === 'delete') deleteInvited.mutate({ clientId: client.id }, options)
    else deactivate.mutate({ clientId: client.id }, options)
  }

  function ask(question: 'delete' | 'deactivate'): void {
    setAsking(question)
    setConfirmError(null)
    setConfirming(true)
  }

  /**
   * The one action that depends on where this person has got to.
   *
   * A pending client shows nothing to a venue admin, rather than falling
   * through to Deactivate: their invitation has not been accepted, so there is
   * no access to block, and withdrawing it is the organization's to do.
   */
  function statusAction(): React.ReactNode {
    if (!canManageClients) return null
    if (client.status === 'DEACTIVATED') {
      return (
        <Button variant="ghost" size="sm" disabled={busy} onClick={() => reactivate.mutate({ clientId: client.id }, said)}>
          Reactivate
        </Button>
      )
    }
    if (client.status === 'PENDING') {
      if (!canDeleteClients) return null
      // Deleting the person, not unlinking them from this event — that is the
      // X beside it. Said in the label, because next to "Remove" the word
      // "Delete" alone would be read as the same thing.
      return (
        <Button
          variant="ghost"
          size="sm"
          className="text-danger-ink hover:text-danger-ink"
          aria-label={`Delete ${client.name}'s invitation`}
          disabled={busy}
          onClick={() => ask('delete')}
        >
          <Trash2 className="h-4 w-4" /> Delete invite
        </Button>
      )
    }
    return (
      <Button
        variant="ghost"
        size="sm"
        className="text-danger-ink hover:text-danger-ink"
        disabled={busy}
        onClick={() => ask('deactivate')}
      >
        Deactivate
      </Button>
    )
  }

  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{client.name}</p>
        <p className="truncate text-xs text-ink-muted">{client.email}</p>
        {canManageClients ? (
          <ClientPhone key={client.phone ?? ''} eventId={event.id} client={client} />
        ) : client.phone ? (
          <p className="truncate text-xs text-ink-muted">{client.phone}</p>
        ) : null}
        {msg ? <FormError size="sm">{msg}</FormError> : null}
      </div>
      <UserStatusBadge
        status={client.status}
        invitedAt={client.invitedAt}
        inviteExpiresAt={client.inviteExpiresAt}
      />
      <div className="flex items-center gap-1.5">
        {client.status === 'PENDING' && canManageClients ? (
          <Button
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={() => invite.mutate({ clientId: client.id }, said)}
          >
            <Mail className="h-4 w-4" /> {inviteActionLabel(client.invitedAt)}
          </Button>
        ) : null}
        {statusAction()}
        {canEdit ? (
          <>
            {/* For a planner an import brought in as a client (decision 32). The
                warning says what it does before anything happens. */}
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => setConverting(true)}>
              <Briefcase className="h-4 w-4" /> This is a planner
            </Button>
            <Button
              variant="ghost"
              size="sm"
              aria-label={`Remove ${client.name}`}
              disabled={busy}
              onClick={() => remove.mutate({ clientId: client.id }, said)}
            >
              <X className="h-4 w-4" />
            </Button>
          </>
        ) : null}
      </div>
      {converting ? (
        <ClientToPlannerDialog
          event={event}
          client={client}
          open={converting}
          onOpenChange={setConverting}
          onInvitationUnsent={() => onPlannerUnsent(client.name)}
        />
      ) : null}
      <ConfirmDialog
        open={confirming}
        onOpenChange={(next) => {
          if (next || deleteInvited.isPending || deactivate.isPending) return
          setConfirming(false)
        }}
        title={asking === 'delete' ? `Delete ${possessive(client.name)} invitation?` : `Deactivate ${client.name}?`}
        description={
          asking === 'delete'
            ? `This deletes ${client.name} rather than taking them off this event: they come off every event they are on, and the invitation stops working.`
            : `${client.name} is signed out and can no longer sign in, to this event or any other. They stay on their events, and can be reactivated.`
        }
        confirmLabel={asking === 'delete' ? 'Delete invitation' : 'Deactivate'}
        destructive
        busy={deleteInvited.isPending || deactivate.isPending}
        error={confirmError}
        onConfirm={confirmStatusChange}
      />
    </li>
  )
}

/**
 * Manage an event's client contacts (staff): list contacts with invite status,
 * add an existing client or create a new one (find-or-create by email), and run
 * the invite lifecycle per contact — an unaccepted invitation is deleted, an
 * accepted account deactivated. Staff who can do neither see the list.
 */
export function EventClientsPanel({
  event,
  canEdit,
  canManageClients,
  canFindClients,
}: {
  event: EventDetailDto
  /** Whether the viewer can edit events, and so add contacts to this one, take them off, or call one a planner. */
  canEdit: boolean
  /** Whether the viewer can edit clients, and so invite, deactivate and reactivate them, and set their phone. */
  canManageClients: boolean
  /** Whether the viewer can view clients, and so search the company's for one to add. */
  canFindClients: boolean
}) {
  const add = useAddEventClient(event.id)
  const [q, setQ] = React.useState('')
  const search = useClientSearch(q)
  const [name, setName] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [phone, setPhone] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  // Kept here, not on the row: a conversion takes the person off the client list, and their row with it.
  const [unsentPlanner, setUnsentPlanner] = React.useState<string | null>(null)

  const existingIds = new Set(event.clients.map((c) => c.id))
  const candidates = (search.data?.users ?? []).filter((u) => !existingIds.has(u.id))

  function addExisting(clientId: string): void {
    setError(null)
    add.mutate({ clientId }, { onError: (e) => setError(requestErrorMessage(e)) })
  }

  function addNew(): void {
    setError(null)
    // The new-contact member, not the whole union: a union reports only that no
    // branch matched, which is not something this form can put beside a field.
    const parsed = newEventClientInputSchema.safeParse({ name, email, ...(phone.trim() !== '' ? { phone } : {}) })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Enter a name and a valid email.')
      return
    }
    add.mutate(parsed.data, {
      onSuccess: () => {
        setName('')
        setEmail('')
        setPhone('')
      },
      onError: (e) => setError(requestErrorMessage(e)),
    })
  }

  /** The search's answer: still looking, refused, nothing left to add, or the people to add. */
  function searchResults(): React.ReactNode {
    if (search.isPending) {
      return (
        <li className="flex items-center gap-2 px-3 py-2 text-sm text-ink-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Searching…
        </li>
      )
    }
    if (search.isError) {
      return (
        <li className="px-3 py-2">
          <FormError size="sm">{requestErrorMessage(search.error)}</FormError>
        </li>
      )
    }
    if (candidates.length === 0) {
      return <li className="px-3 py-2 text-sm text-ink-muted">No matching clients not already on this event.</li>
    }
    return candidates.map((u) => (
      <li key={u.id} className="flex items-center gap-3 px-3 py-2">
        <Users className="h-4 w-4 shrink-0 text-ink-muted" />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium">{u.name}</span>
          <span className="block truncate text-xs text-ink-muted">
            {u.email}
            {u.phone ? ` · ${u.phone}` : ''}
          </span>
        </span>
        <Button size="sm" variant="outline" disabled={add.isPending} onClick={() => addExisting(u.id)}>
          Add
        </Button>
      </li>
    ))
  }

  return (
    <div className="space-y-5">
      {unsentPlanner !== null ? (
        <Callout
          tone="attention"
          title={`${unsentPlanner} is now the planner, but their invitation could not be sent.`}
          action={
            <Button variant="ghost" size="sm" onClick={() => setUnsentPlanner(null)}>
              Dismiss
            </Button>
          }
        >
          Send it from the event’s Planner section.
        </Callout>
      ) : null}
      {event.clients.length === 0 ? (
        <p className="rounded-md border border-line px-4 py-3 text-sm text-ink-muted">
          No client contacts yet.
          {canEdit ? ' Add one below — they can then be invited to sign in and arrange the floor plan.' : null}
        </p>
      ) : (
        <ul className="divide-y divide-line overflow-hidden rounded-md border border-line">
          {event.clients.map((c) => (
            <ContactRow
              key={c.id}
              event={event}
              client={c}
              canEdit={canEdit}
              canManageClients={canManageClients}
              onPlannerUnsent={setUnsentPlanner}
            />
          ))}
        </ul>
      )}

      {canEdit ? (
        <Card className="space-y-3 p-4">
          <p className="flex items-center gap-2 text-sm font-medium">
            <UserPlus className="h-4 w-4 text-ink-muted" /> Add a contact
          </p>
          {/* Looking up the company's clients needs Clients View; anyone adding a contact can still add one by name. */}
          {canFindClients ? (
            <>
              <Field label="Search existing clients" htmlFor="client-search">
                <Input id="client-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or email" />
              </Field>
              {q.trim() !== '' ? (
                <ul className="divide-y divide-line overflow-hidden rounded-md border border-line">
                  {searchResults()}
                </ul>
              ) : null}
            </>
          ) : null}

          <div className="grid gap-3 sm:grid-cols-[1fr_1fr_0.8fr_auto] sm:items-end">
            <Field label="New contact name" htmlFor="client-new-name">
              <Input id="client-new-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" />
            </Field>
            <Field label="Email" htmlFor="client-new-email">
              <Input id="client-new-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
            </Field>
            <Field label="Phone (optional)" htmlFor="client-new-phone">
              <Input id="client-new-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="off" />
            </Field>
            <Button pending={add.isPending} type="button" disabled={name.trim() === '' || email.trim() === ''} onClick={addNew}>
              <UserPlus className="h-4 w-4" />
              Add
            </Button>
          </div>
          {error ? <FormError>{error}</FormError> : null}
        </Card>
      ) : null}

      {/* What has actually been sent to these people. Under the list they
          belong to, because "did she get the invitation?" is asked about a
          contact somebody is already looking at. */}
      <EventEmailLog eventId={event.id} />
    </div>
  )
}
